/**
 * LEED for existing buildings, as a self-assessment the airport enters
 * category by category, with the platform's own figures shown beside the
 * categories they inform. The maxima are the published LEED v4 Operations
 * and Maintenance: Existing Buildings scheme; the rating system version in
 * force should be checked before an application, and the doc says so.
 */
export interface LeedCategory {
  key: string;
  label: string;
  max: number;
  /** which platform figures bear on it */
  informedBy: string;
}

export const LEED_CATEGORIES: LeedCategory[] = [
  { key: "lt", label: "Location and transportation", max: 15, informedBy: "staff and passenger surface access — entered" },
  { key: "ss", label: "Sustainable sites", max: 10, informedBy: "site management, light pollution, rainwater — entered" },
  { key: "we", label: "Water efficiency", max: 12, informedBy: "the green programme's water intensity and recycling share" },
  { key: "ea", label: "Energy and atmosphere", max: 38, informedBy: "the green programme's energy intensity, renewable share and inventory" },
  { key: "mr", label: "Materials and resources", max: 8, informedBy: "waste tonnes and the diverted share" },
  { key: "eq", label: "Indoor environmental quality", max: 17, informedBy: "air quality and thermal comfort — entered" },
  { key: "in", label: "Innovation", max: 6, informedBy: "entered" },
  { key: "rp", label: "Regional priority", max: 4, informedBy: "entered" },
];

export const LEED_LEVELS: { label: string; min: number }[] = [
  { label: "Platinum", min: 80 },
  { label: "Gold", min: 60 },
  { label: "Silver", min: 50 },
  { label: "Certified", min: 40 },
];

export interface LeedEntered {
  points?: Record<string, number>;
  targetLevel?: string;
  enteredBy?: string;
  enteredAt?: string;
}

export interface LeedStanding {
  total: number;
  max: number;
  level: string | null;
  nextLevel: { label: string; needs: number } | null;
  categories: { category: LeedCategory; points: number; pct: number }[];
  /** the categories with the most points still available */
  headroom: { category: LeedCategory; available: number }[];
}

export function leedStanding(e: LeedEntered): LeedStanding {
  const categories = LEED_CATEGORIES.map((c) => {
    const points = Math.max(0, Math.min(c.max, e.points?.[c.key] ?? 0));
    return { category: c, points, pct: Math.round((100 * points) / c.max) };
  });
  const total = categories.reduce((t, c) => t + c.points, 0);
  const max = LEED_CATEGORIES.reduce((t, c) => t + c.max, 0);
  const level = LEED_LEVELS.find((l) => total >= l.min)?.label ?? null;
  const next = [...LEED_LEVELS].reverse().find((l) => total < l.min);
  const headroom = [...categories].map((c) => ({ category: c.category, available: c.category.max - c.points })).sort((a, b) => b.available - a.available).slice(0, 3);
  return { total, max, level, nextLevel: next ? { label: next.label, needs: next.min - total } : null, categories, headroom };
}
